/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { extractError } from '@app/common/utils';
import { ExtractedErrorText } from '@app/core/types';
import { Store } from '@ngrx/store';
import { ContactUsRequest } from '@shared/types/common';
import { catchError, EMPTY, merge, Observable, of, switchMap, tap } from 'rxjs';
import { ContactUsApiService } from './contact-us-api.service';
import { DIALOG_CONTACT_US_ID } from './dialog-contact-us.component';
import { sendContactUsRequestFailure, sendContactUsRequestSuccess } from './ngrx/contact-us.actions';

export type ContactUsRequestProgressEvent = { type: 'progress'; loaded: number; total: number };
export type ContactUsSuccessEvent = { type: 'success' };
export type ContactUsErrorEvent = { type: 'error'; message: ExtractedErrorText };
export type ContactUsEvent = ContactUsRequestProgressEvent | ContactUsSuccessEvent | ContactUsErrorEvent;

@Injectable({
  providedIn: 'root',
})
export class ContactUsService {
  private api = inject(ContactUsApiService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  sendSupportRequest(data: ContactUsRequest, files: File[]): Observable<ContactUsEvent> {
    // The first progress event might take a while, the network speed matters
    const firstProgressEvent = of({ type: 'progress', total: Infinity, loaded: 0 } as const);
    const response = this.api.sendRequestWithFiles(data, files).pipe(
      switchMap((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            return of({ type: 'progress', total: event.total ?? Infinity, loaded: event.loaded } as const);
          case HttpEventType.Response:
            return of({ type: 'success' } as const);
          default:
            return EMPTY;
        }
      }),
      catchError((err) => of({ type: 'error', message: extractError(err, 'Failed to send support request') } as const)),
      tap((event) => {
        if (event.type === 'success') {
          this.dialog.getDialogById(DIALOG_CONTACT_US_ID)?.close();
          this.store.dispatch(
            sendContactUsRequestSuccess({
              notification: `Thank you for your submission. We will contact you soon.`,
            }),
          );
        }
        if (event.type === 'error') {
          this.store.dispatch(sendContactUsRequestFailure({ silent: true, error: event.message }));
        }
      }),
    );
    return merge(firstProgressEvent, response);
  }
}
